import React, { useState, useEffect, useMemo } from 'react';
import { RefreshCw } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import ProductCard from '../components/ProductCard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const accessoryCategories = [
  { id: 'all', name: { hu: 'Összes', en: 'All' } },
  { id: 'bowl', name: { hu: 'Dohánytölcsérek', en: 'Tobacco Bowls' } },
  { id: 'coalholder', name: { hu: 'Széntartó konzolok', en: 'Coal Holder Consoles' } },
  { id: 'coalcarrier', name: { hu: 'Szén hordozók', en: 'Coal Carriers' } },
  { id: 'mouthpiece', name: { hu: 'Szipkák', en: 'Mouthpieces' } },
  { id: 'handmade', name: { hu: 'Kézműves dohánytartók/szipkák', en: 'Handcrafted Bowls/Mouthpieces' } },
  { id: 'tip', name: { hu: 'Szívóvégek', en: 'Hose Tips' } },
  { id: 'tongs', name: { hu: 'Csipeszek', en: 'Tongs' } },
  { id: 'heatcap', name: { hu: 'Hőtartó sapkák', en: 'Heat Caps' } },
  { id: 'hose', name: { hu: 'Szívócsövek', en: 'Hoses' } },
  { id: 'fork', name: { hu: 'Dohányvilla és alufólia-lyukasztó', en: 'Tobacco Fork & Foil Punch' } },
  { id: 'flask', name: { hu: 'Víztartályok', en: 'Water Tanks' } },
  { id: 'bag', name: { hu: 'Táskák', en: 'Bags' } },
  { id: 'cleaner', name: { hu: 'Tisztítók', en: 'Cleaners' } },
  { id: 'burner', name: { hu: 'Szénizzítók', en: 'Coal Burners' } },
  { id: 'other', name: { hu: 'Egyéb', en: 'Other' } },
];

const AccessoriesPage = () => {
  const { t, getLocalizedText } = useLanguage();
  const [sortBy, setSortBy] = useState('default');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`, {
          params: { category: 'accessory' }
        });
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const sortedProducts = useMemo(() => {
    let filtered = [...products];
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => p.subcategory === selectedCategory);
    }

    switch (sortBy) {
      case 'price-asc':
        return filtered.sort((a, b) => a.price - b.price);
      case 'price-desc':
        return filtered.sort((a, b) => b.price - a.price);
      case 'name-asc':
        return filtered.sort((a, b) => 
          getLocalizedText(a.name).localeCompare(getLocalizedText(b.name))
        );
      case 'name-desc':
        return filtered.sort((a, b) => 
          getLocalizedText(b.name).localeCompare(getLocalizedText(a.name))
        );
      default:
        return filtered;
    }
  }, [sortBy, selectedCategory, getLocalizedText, products]);

  return (
    <div className="min-h-screen bg-black py-8 px-4">
      <div className="container mx-auto">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-4xl font-bold text-white mb-2 md:mb-4">{t.nav.accessories}</h1>
          <p className="text-zinc-400 text-sm md:text-base">{t.categories.accessoriesDesc}</p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 overflow-x-auto pb-2">
          {accessoryCategories.map((cat) => (
            <Button
              key={cat.id}
              variant={selectedCategory === cat.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(cat.id)}
              className={`whitespace-nowrap text-xs sm:text-sm ${selectedCategory === cat.id 
                ? 'bg-white text-black hover:bg-zinc-200' 
                : 'border-zinc-700 text-zinc-400 hover:bg-zinc-800 hover:text-white'
              }`}
            >
              {getLocalizedText(cat.name)}
            </Button>
          ))}
        </div>

        {/* Sort Filter */}
        <div className="flex flex-wrap gap-4 mb-8 p-4 bg-zinc-900 rounded-lg">
          <div className="flex items-center gap-2">
            <span className="text-sm text-zinc-400">{t.filters.sortBy}:</span>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px] bg-zinc-800 border-zinc-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="default" className="text-white hover:bg-zinc-700">{t.filters.all}</SelectItem>
                <SelectItem value="price-asc" className="text-white hover:bg-zinc-700">{t.filters.priceAsc}</SelectItem>
                <SelectItem value="price-desc" className="text-white hover:bg-zinc-700">{t.filters.priceDesc}</SelectItem>
                <SelectItem value="name-asc" className="text-white hover:bg-zinc-700">{t.filters.nameAsc}</SelectItem>
                <SelectItem value="name-desc" className="text-white hover:bg-zinc-700">{t.filters.nameDesc}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-20">
            <RefreshCw className="h-8 w-8 animate-spin text-zinc-500 mx-auto mb-4" />
            <p className="text-zinc-500">{t.common.loading}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
              {sortedProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>

            {sortedProducts.length === 0 && (
              <div className="text-center py-20">
                <p className="text-zinc-500 text-lg">{t.common.noResults}</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AccessoriesPage;
